import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { formatApiError } from "../lib/api";
import { Building2, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/portfolio" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left: Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/15693274/pexels-photo-15693274.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
          alt="Dubai skyline"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent" />
        <div className="absolute bottom-12 left-12 max-w-md">
          <h2 className="font-serif text-3xl text-white mb-3">
            Your Global Property Empire,<br />One Dashboard.
          </h2>
          <p className="text-white/65 text-sm font-sans">
            Manage assets across Dubai, North Cyprus & Germany with real-time FX, AI document analysis, and regulatory intelligence.
          </p>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex items-center justify-center bg-[#050505] px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="mb-10">
            <img src="/equia-logo.png" alt="EQUIA Global Property" className="h-20 w-auto" data-testid="login-equia-logo" />
          </div>

          <h2 className="font-serif text-xl text-white mb-1">
            Welcome Back
          </h2>
          <p className="text-white/60 text-sm mb-8 font-sans">
            Sign in to your portfolio
          </p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 text-sm mb-6" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-[11px] tracking-[0.15em] uppercase text-white/65 font-sans mb-2 block">Email</label>
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 text-sm font-sans placeholder:text-white/55 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-all outline-none"
                placeholder="investor@example.com"
                required
              />
            </div>

            <div>
              <label className="text-[11px] tracking-[0.15em] uppercase text-white/65 font-sans mb-2 block">Password</label>
              <div className="relative">
                <input
                  data-testid="password-input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 text-sm font-sans placeholder:text-white/55 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-all outline-none pr-12"
                  placeholder="Min. 6 characters"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/65 hover:text-white/85 transition-colors"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="mt-2 text-right">
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-white/60 text-xs hover:text-[#D4AF37] transition-colors font-sans"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] text-black font-medium py-3 text-sm tracking-wide hover:bg-[#B5952F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Please wait..." : "Sign In"}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-white/[0.06]" />
            <span className="text-white/55 text-[10px] tracking-[0.15em] uppercase">or</span>
            <div className="flex-1 h-px bg-white/[0.06]" />
          </div>

          {/* Google OAuth Button */}
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <button
            data-testid="google-auth-btn"
            onClick={() => {
              const redirectUrl = window.location.origin + '/';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full border border-white/10 text-white py-3 text-sm font-sans hover:bg-white/[0.03] hover:border-white/20 transition-all flex items-center justify-center gap-3"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div className="mt-6 text-center">
            <p
              data-testid="login-invite-only-note"
              className="text-white/60 text-sm font-sans"
            >
              Invite-only beta. <Link to="/" className="text-[#D4AF37] hover:text-[#B5952F] transition-colors">Request access</Link>
            </p>
          </div>
        </motion.div>

        {/* Footer links */}
        <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-6">
          <a href="/legal" className="text-white/55 text-xs hover:text-[#D4AF37] transition-colors" data-testid="login-legal-link">Legal Notice</a>
          <a href="/privacy" className="text-white/55 text-xs hover:text-[#D4AF37] transition-colors" data-testid="login-privacy-link">Privacy Policy</a>
        </div>
      </div>
    </div>
  );
}
